import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, AlertTriangle, CheckCircle2, Download, PoundSterling, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { listQuotes, exportUrl, getPricingSettings } from "@/lib/api";
import { formatCurrency, flexMatch } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";
import PaymentReceiptModal from "@/components/custom/PaymentReceiptModal";
import QbSyncChip from "@/components/custom/QbSyncChip";

function daysSince(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (isNaN(d)) return 0;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

function getChaseStatus(days, clientType) {
  const isPrivate = clientType === "Private";
  const chase1 = isPrivate ? 3 : 20;
  const chase2 = isPrivate ? 10 : 30;
  if (days >= 30) return { level: 3, label: "Overdue 30+", color: "badge-danger" };
  if (days >= chase2) return { level: 2, label: "2nd Chase Due", color: "badge-danger" };
  if (days >= chase1) return { level: 1, label: "1st Chase Due", color: "badge-warning" };
  return { level: 0, label: "Current", color: "badge-success" };
}

export default function Invoices() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payDialog, setPayDialog] = useState(null);
  const [pricingRules, setPricingRules] = useState({});
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");
  const [filterChases, setFilterChases] = useState(false);
  const [filterOverdue, setFilterOverdue] = useState(false);

  const fetchInvoices = () => {
    setLoading(true);
    listQuotes().then(data => {
      setQuotes(data.filter(q => q.status === "Invoiced" || q.status === "Partially Paid" || q.status === "Paid" || q.invoice_paid));
    }).catch(() => toast.error("Failed to load"))
    .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInvoices(); }, []);
  useEffect(() => {
    getPricingSettings().then(d => setPricingRules(d?.rules || d || {})).catch(() => {});
  }, []);

  // Date filter helper
  const inDateRange = (dateStr) => {
    if (!filterFrom && !filterTo) return true;
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      if (isNaN(d)) return false;
      const ts = d.getTime();
      if (filterFrom) { const from = new Date(filterFrom); from.setHours(0,0,0,0); if (ts < from.getTime()) return false; }
      if (filterTo) { const to = new Date(filterTo); to.setHours(23,59,59,999); if (ts > to.getTime()) return false; }
      return true;
    } catch { return false; }
  };

  const filtered = useMemo(() => {
    let result = quotes;
    // Date range filter
    if (filterFrom || filterTo) {
      result = result.filter(q => {
        if (q.invoice_paid) return inDateRange(q.invoice_paid_date);
        return inDateRange(q.updated_at);
      });
    }
    // Search filter
    if (search) {
      result = result.filter(q => {
        const combined = [q.site_address, q.client_type, q.client_first_name, q.client_last_name, (q.quote_number || "").replace("Q-", "I-"), q.quote_number, String(q.total_inc_vat || "")].filter(Boolean).join(" ");
        return flexMatch(combined, search);
      });
    }
    // Chase filter
    if (filterChases) {
      result = result.filter(q => {
        if (q.invoice_paid) return false;
        const days = daysSince(q.updated_at);
        return getChaseStatus(days, q.client_type).level >= 1;
      });
    }
    // Overdue 30+ filter
    if (filterOverdue) {
      result = result.filter(q => {
        if (q.invoice_paid) return false;
        return daysSince(q.updated_at) >= 30;
      });
    }
    return result;
  }, [quotes, filterFrom, filterTo, search, filterChases, filterOverdue]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (a.invoice_paid && !b.invoice_paid) return 1;
      if (!a.invoice_paid && b.invoice_paid) return -1;
      return daysSince(b.updated_at) - daysSince(a.updated_at);
    });
  }, [filtered]);

  const stats = useMemo(() => {
    const unpaid = filtered.filter(q => !q.invoice_paid);
    const paid = filtered.filter(q => q.invoice_paid);
    const totalUnpaid = unpaid.reduce((s, q) => s + (q.total_inc_vat || 0), 0);
    const totalPaid = paid.reduce((s, q) => s + (q.invoice_paid_amount || q.total_inc_vat || 0), 0);
    let chasesDue = 0;
    unpaid.forEach(q => { if (getChaseStatus(daysSince(q.updated_at), q.client_type).level > 0) chasesDue++; });
    return { total: filtered.length, unpaid: unpaid.length, paid: paid.length, totalUnpaid, totalPaid, chasesDue };
  }, [filtered]);

  const handleSaved = () => {
    setPayDialog(null);
    fetchInvoices();
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <div className="flex items-center justify-between mb-6">
          <h1 className={isMobile ? "text-lg font-semibold" : "ui-page-title"}>Invoices</h1>
          {!isMobile && (
            <a href={`${exportUrl("invoices")}${(filterFrom || filterTo) ? `?${[filterFrom ? `date_from=${filterFrom}` : "", filterTo ? `date_to=${filterTo}` : ""].filter(Boolean).join("&")}` : ""}`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="invoices-export-csv">
              <Download className="w-4 h-4" /> Export CSV
            </a>
          )}
        </div>

        {/* Date filter */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Filter:</span>
          <div className="relative">
            <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="w-44 h-9 text-sm pl-3 pr-8" style={{ border: "1px solid var(--border)", borderRadius: "6px", padding: "8px 12px" }} data-testid="invoice-filter-from" />
          </div>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>to</span>
          <div className="relative">
            <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="w-44 h-9 text-sm pl-3 pr-8" style={{ border: "1px solid var(--border)", borderRadius: "6px", padding: "8px 12px" }} data-testid="invoice-filter-to" />
          </div>
          {(filterFrom || filterTo) && (
            <button onClick={() => { setFilterFrom(""); setFilterTo(""); }} className="text-xs font-semibold" style={{ color: "var(--danger)" }}>Clear</button>
          )}
        </div>

        {/* Search bar */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by address, client name, invoice number, amount..." className="w-full h-10 pl-10 pr-10 rounded-lg text-sm" style={{ border: "1px solid var(--border)", background: "var(--surface)" }} />
          {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }}><X className="w-4 h-4" /></button>}
        </div>

        {/* Filter pills */}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setFilterChases(!filterChases)} className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={filterChases ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>Chases Due</button>
          <button onClick={() => setFilterOverdue(!filterOverdue)} className="px-3 py-1.5 rounded-full text-xs font-semibold transition-colors" style={filterOverdue ? { background: "#E05C2A", color: "#fff" } : { background: "var(--surface)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>Overdue 30+</button>
        </div>

        {/* Stats */}
        <div className={`grid ${isMobile ? "grid-cols-2" : "grid-cols-4"} gap-3 mb-6`}>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Unpaid</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1" style={{ color: stats.unpaid > 0 ? "var(--danger)" : "var(--text-primary)" }}>{stats.unpaid}</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{formatCurrency(stats.totalUnpaid)}</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Paid</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1" style={{ color: "var(--success)" }}>{stats.paid}</p>
            <p className="text-xs mt-0.5" style={{ color: "var(--success)" }}>{formatCurrency(stats.totalPaid)}</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Chases Due</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1" style={{ color: stats.chasesDue > 0 ? "var(--danger)" : "var(--text-primary)" }}>{stats.chasesDue}</p>
          </div>
          <div className="ui-surface p-3 md:p-4">
            <p className="ui-label text-[10px] md:text-xs">Total Invoices</p>
            <p className="text-lg md:text-2xl font-mono font-bold mt-1">{stats.total}</p>
          </div>
        </div>
      </div>

      <div className={isMobile ? "px-4 pb-4" : "px-8 pb-8"}>
        {loading ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>Loading...</p>
        ) : sorted.length === 0 ? (
          <p className="text-center py-10" style={{ color: "var(--text-muted)" }}>No invoices{(filterFrom || filterTo) ? " in this date range" : ""}</p>
        ) : isMobile ? (
          <div className="space-y-2">
            {sorted.map(q => {
              const days = daysSince(q.updated_at);
              const chase = getChaseStatus(days, q.client_type);
              const isPaid = !!q.invoice_paid;
              return (
                <div key={q.id} className="ui-surface p-3">
                  <div className="flex items-start justify-between gap-2 mb-1.5" onClick={() => navigate(`/quotes/${q.id}`)}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{q.site_address || "—"}</p>
                      <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{[q.client_first_name, q.client_last_name].filter(Boolean).join(" ")} · {q.client_type}</p>
                    </div>
                    <p className="text-sm font-mono font-bold flex-shrink-0">{formatCurrency(q.total_inc_vat)}</p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {!isPaid && <span className="text-[11px] font-mono" style={{ color: "var(--text-secondary)" }}>{days}d</span>}
                      {!isPaid && <span className={`badge-pill text-[10px] ${chase.color}`}>{chase.label}</span>}
                      {isPaid && <span className="text-xs" style={{ color: "var(--text-muted)" }}>Paid {q.invoice_paid_date ? new Date(q.invoice_paid_date).toLocaleDateString("en-GB") : ""}</span>}
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); if (!isPaid) setPayDialog(q); }} className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors" style={isPaid ? { background: "var(--success)", color: "#fff" } : { background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)" }} disabled={isPaid} data-testid={`mark-paid-${q.id}`}>
                      {isPaid ? (q.payment_status === "partial" ? "Partial" : "Paid") : "Mark Paid"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ui-surface overflow-hidden">
            <table className="ui-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Address</th>
                  <th>Client</th>
                  <th className="text-right">Amount</th>
                  <th className="text-center">Days</th>
                  <th className="text-center">Chase</th>
                  <th className="text-center">Payment</th>
                  <th>Paid Date</th>
                  <th className="text-center" title="QuickBooks sync status">QB</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(q => {
                  const days = daysSince(q.updated_at);
                  const chase = getChaseStatus(days, q.client_type);
                  const isPaid = !!q.invoice_paid;
                  return (
                    <tr key={q.id} className={`cursor-pointer ${isPaid ? "opacity-60" : ""}`} onClick={() => navigate(`/quotes/${q.id}`)}>
                      <td className="font-mono font-medium">{q.invoice_number || (q.quote_number || "").replace("Q-", "I-") || "—"}</td>
                      <td className="font-medium max-w-[180px] truncate">{q.site_address || "—"}</td>
                      <td>{[q.client_first_name, q.client_last_name].filter(Boolean).join(" ") || q.client_type}</td>
                      <td className="text-right font-mono font-bold">{formatCurrency(isPaid ? (q.invoice_paid_amount || q.total_inc_vat) : q.total_inc_vat)}</td>
                      <td className="text-center">{isPaid ? <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span> : <span className={`font-mono font-bold ${days >= 30 ? "text-red-500" : days >= 14 ? "text-amber-500" : ""}`}>{days}</span>}</td>
                      <td className="text-center">{q.refund_status === "full" ? <span className="badge-pill badge-danger">Refunded</span> : q.refund_status === "partial" ? <span className="badge-pill badge-warning">Partially Refunded</span> : isPaid ? <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span> : <span className={`badge-pill ${chase.color}`}>{chase.label}</span>}</td>
                      <td className="text-center">
                        {isPaid ? (
                          <div className="inline-flex flex-col items-center gap-0.5">
                            <span className="badge-pill badge-success text-[10px]" data-testid={`paid-badge-${q.id}`}>
                              {q.payment_status === "partial" ? `Partial · ${formatCurrency(q.invoice_paid_amount || 0)} / ${formatCurrency(q.total_inc_vat)}` : "Paid"}
                            </span>
                            {q.shortfall_reason === "commission" && <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>Comm. deducted</span>}
                            {q.shortfall_reason === "discount" && <span className="text-[9px]" style={{ color: "var(--text-muted)" }}>Discount applied</span>}
                          </div>
                        ) : (
                          <button onClick={(e) => { e.stopPropagation(); setPayDialog(q); }} className="inline-flex items-center gap-1 rounded-lg text-xs font-semibold transition-colors" style={{ background: "var(--bg)", color: "var(--text-secondary)", border: "1px solid var(--border)", padding: "8px 16px" }} data-testid={`mark-paid-${q.id}`}>
                            <PoundSterling className="w-3 h-3" /> Mark Paid
                          </button>
                        )}
                      </td>
                      <td className="text-xs" style={{ color: "var(--text-muted)" }}>{isPaid && q.invoice_paid_date ? new Date(q.invoice_paid_date).toLocaleDateString("en-GB") : "—"}</td>
                      <td className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-1">
                          <QbSyncChip quote={q} kind="invoice" onSynced={fetchInvoices} />
                          {isPaid && <QbSyncChip quote={q} kind="payment" onSynced={fetchInvoices} />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Receipt Modal — replaces old Mark Paid dialog */}
      <PaymentReceiptModal
        open={!!payDialog}
        onOpenChange={(v) => { if (!v) setPayDialog(null); }}
        quote={payDialog}
        pricingRules={pricingRules}
        onSaved={handleSaved}
      />
    </div>
  );
}
